/**
 * TextRevealAnimation - 文字逐字显示动画自定义元素
 * 
 * 使用方式:
 * <text-reveal-animation
 *   class="animated-text-container"
 *   letter-delay="50"
 *   start-delay="300"
 *   animation-method="webapi"
 *   viewport-threshold="0.3">
 *   <span class="origin-text">SMART MEETS STYLE</span>
 * </text-reveal-animation>
 */

if (!customElements.get('text-reveal-animation')) {
  class TextRevealAnimation extends HTMLElement {
    static get observedAttributes() {
      return ['letter-delay', 'start-delay', 'animation-method', 'viewport-threshold', 'animate-once'];
    }

    constructor() {
      super();
      
      // 默认配置
      this._config = {
        letterDelay: 50,
        animationDuration: 600,
        startDelay: 0,
        animationMethod: 'webapi',
        viewportThreshold: 0.3,
        animateOnce: true
      };

      this._observer = null;
      this._animationFrames = [];
      this._isAnimated = false;
    }

    connectedCallback() {
      // 从属性读取配置
      this._updateConfigFromAttributes();
      
      // 设置视口观察器
      this._setupIntersectionObserver();
    }

    disconnectedCallback() {
      this._cleanup();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      
      switch (name) {
        case 'letter-delay':
          this._config.letterDelay = parseInt(newValue) || 50;
          break;
        case 'start-delay':
          this._config.startDelay = parseInt(newValue) || 0;
          break;
        case 'animation-method':
          this._config.animationMethod = newValue || 'webapi';
          break;
        case 'viewport-threshold':
          this._config.viewportThreshold = parseFloat(newValue) || 0.3;
          break;
        case 'animate-once':
          this._config.animateOnce = newValue !== 'false';
          break;
      }
    }

    /**
     * 从元素属性更新配置
     */
    _updateConfigFromAttributes() {
      if (this.hasAttribute('letter-delay')) {
        this._config.letterDelay = parseInt(this.getAttribute('letter-delay')) || 50;
      }
      if (this.hasAttribute('start-delay')) {
        this._config.startDelay = parseInt(this.getAttribute('start-delay')) || 0;
      }
      if (this.hasAttribute('animation-method')) {
        this._config.animationMethod = this.getAttribute('animation-method') || 'webapi';
      }
      if (this.hasAttribute('viewport-threshold')) {
        this._config.viewportThreshold = parseFloat(this.getAttribute('viewport-threshold')) || 0.3;
      }
      if (this.hasAttribute('animate-once')) {
        this._config.animateOnce = this.getAttribute('animate-once') !== 'false';
      }
    }

    /**
     * 设置视口交叉观察器
     */
    _setupIntersectionObserver() {
      this._observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this._isAnimated) {
            this.play();
            
            if (this._config.animateOnce) {
              this._observer.unobserve(this);
            }
          }
        });
      }, {
        threshold: this._config.viewportThreshold,
        rootMargin: '0px 0px -10% 0px'
      });

      this._observer.observe(this);
    }

    /**
     * 开始动画
     */
    _startAnimation() {
      this._isAnimated = true;
      
      switch (this._config.animationMethod) {
        case 'css':
          this._animateTextPureCSS();
          break;
        case 'batch':
          this._animateTextBatch();
          break;
        case 'webapi':
        default:
          this._animateTextWebAPI();
          break;
      }
    }

    /**
     * 将文字分割成字母（保留换行）
     * @returns {Array} 所有字母元素的数组
     */
    _splitTextToLetters() {
      const originElement = this.querySelector('.origin-text');
      if (!originElement) return [];
      
      const originalHTML = originElement.innerHTML;
      const hasBrTags = /<br\s*\/?>/i.test(originalHTML);
      
      // 清空并重建
      this.innerHTML = '';
      this.classList.add('animated-text');
      
      let letterIndex = 0;
      const allLetters = [];
      
      // 按 <br> 分割成行
      const lines = originalHTML.split(/<br\s*\/?>/i);
      
      lines.forEach((line, lineIndex) => {
        const lineContainer = document.createElement('span');
        lineContainer.className = 'animated-text-line';
        lineContainer.style.opacity = '0';
        lineContainer.setAttribute('data-line-index', lineIndex);
        
        // 获取纯文本
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = line;
        const text = tempDiv.textContent || tempDiv.innerText;
        
        // 分割文本为字母
        text.split('').forEach((char) => {
          if (char === '\n' || char === '\r') return;
          
          const span = document.createElement('span');
          span.className = 'letter';
          
          if (char === ' ') {
            span.innerHTML = '&nbsp;';
          } else {
            span.textContent = char;
          }
          
          span.dataset.index = letterIndex;
          span.style.setProperty('--letter-index', letterIndex);
          // span.style.opacity = '0';
          // span.style.visibility = 'hidden';
          
          lineContainer.appendChild(span);
          allLetters.push(span);
          letterIndex++;
        });
        
        this.appendChild(lineContainer);
      });
      
      return allLetters;
    }

    /**
     * 纯 CSS 版本动画
     */
    _animateTextPureCSS() {
      let letters = this.querySelectorAll('.letter');
      if (letters.length === 0) {
        letters = this._splitTextToLetters();
      }

      // 设置 CSS 变量
      this.style.setProperty('--letter-delay', `${this._config.letterDelay}ms`);
      this.style.setProperty('--animation-duration', `${this._config.animationDuration}ms`);
      this.style.setProperty('--start-delay', `${this._config.startDelay}ms`);
      
      requestAnimationFrame(() => {
        this.classList.add('animating');
        letters.forEach(letter => letter.classList.add('show'));
      });
    }

    /**
     * 批处理版本动画
     */
    _animateTextBatch() {
      let letters = this.querySelectorAll('.letter');
      if (letters.length === 0) {
        letters = this._splitTextToLetters();
      }
      
      const letterArray = Array.from(letters);
      
      // 预先计算所有时间点
      const timings = letterArray.map((letter, index) => ({
        letter,
        delay: this._config.startDelay + (index * this._config.letterDelay)
      }));

      timings.sort((a, b) => a.delay - b.delay);

      let currentIndex = 0;
      const startTime = performance.now();

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        
        while (currentIndex < timings.length && elapsed >= timings[currentIndex].delay) {
          timings[currentIndex].letter.classList.add('show');
          currentIndex++;
        }
        
        if (currentIndex < timings.length) {
          const rafId = requestAnimationFrame(animate);
          this._animationFrames.push(rafId);
        }
      };
      
      const rafId = requestAnimationFrame(animate);
      this._animationFrames.push(rafId);
    }

    /**
     * Web Animations API 版本
     */
    _animateTextWebAPI() {
      let letters = this.querySelectorAll('.letter');
      if (letters.length === 0) {
        letters = this._splitTextToLetters();
      }
      
      // 显示行容器
      this.querySelectorAll('.animated-text-line').forEach(e => {
        e.style.opacity = '1';
        e.style.visibility = 'visible';
      });
      
      const animations = [];
      
      letters.forEach((letter, index) => {
        const animation = letter.animate([
          {
            opacity: 0,
            transform: 'translateY(10px)',
            filter: 'blur(10px)',
            visibility: 'hidden'
          },
          {
            opacity: 1,
            transform: 'translateY(0)',
            filter: 'blur(0)',
            visibility: 'visible'
          }
        ], {
          duration: this._config.animationDuration,
          delay: this._config.startDelay + (index * this._config.letterDelay),
          easing: 'ease-out',
          fill: 'none'
        });

        animation.onfinish = () => {
          letter.classList.add('animation-complete');
        };
        
        animations.push(animation);
      });
      
      Promise.all(animations.map(a => a.finished)).then(() => {
        this._isAnimated = false;
      });
    }

    /**
     * 重置动画
     */
    reset() {
      // 取消所有正在进行的动画帧
      this._animationFrames.forEach(id => cancelAnimationFrame(id));
      this._animationFrames = [];

      const letters = this.querySelectorAll('.letter');
      letters.forEach(letter => {
        letter.getAnimations().forEach(anim => {
          anim.finish();
          anim.cancel();
        });
        
        letter.style.opacity = '0';
        letter.style.visibility = 'hidden';
        letter.style.transform = 'translateY(10px)';
        letter.style.filter = 'blur(10px)';
        letter.style.animation = 'none';
        letter.style.cssText = '';
        
        letter.classList.remove('show', 'animation-complete');
      });
      
      this.classList.remove('animating');
      this._isAnimated = false;
    }

    /**
     * 手动触发动画
     */
    play() {
      if (!this._isAnimated) {
        this.reset();
        this._startAnimation();
      }
    }

    /**
     * 清理资源
     */
    _cleanup() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      
      this._animationFrames.forEach(id => cancelAnimationFrame(id));
      this._animationFrames = [];
    }

    // Getter/Setter for properties
    get letterDelay() {
      return this._config.letterDelay;
    }

    set letterDelay(value) {
      this._config.letterDelay = parseInt(value) || 50;
      this.setAttribute('letter-delay', this._config.letterDelay);
    }

    get startDelay() {
      return this._config.startDelay;
    }

    set startDelay(value) {
      this._config.startDelay = parseInt(value) || 0;
      this.setAttribute('start-delay', this._config.startDelay);
    }

    get animationMethod() {
      return this._config.animationMethod;
    }

    set animationMethod(value) {
      this._config.animationMethod = value || 'webapi';
      this.setAttribute('animation-method', this._config.animationMethod);
    }
  }

  customElements.define('text-reveal-animation', TextRevealAnimation);
}
